<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Every sovereign country has its own laws, and Australia is no exception. Watch the video below
      to find out a little about how Australia’s legal system is organised.
    </p>
    <centered-image-wrapper
      :image="videos[1].image"
      classes="2/3"
      class="cursor-pointer"
      label="Play video: The Australian Legal System"
      @clicked="showIframePopup(videos[1].video)"
    />

    <p class="mb-5">
      According to Separation of Powers, the government is divided into three branches:
    </p>
    <ul class="default-list">
      <li>The Legislature</li>
      <li>The Executive</li>
      <li>The Judiciary</li>
    </ul>
    <p class="mb-5">
      Each of these arms of government is responsible for a different aspect of the law.
    </p>
    <p class="mb-5">Look at the three branches of government listed in the table below.</p>

    <h4 class="mb-5">Task - Questions:</h4>

    <p class="mb-5">
      <strong
        >1. Can you match each branch with the correct function from the options provided
        here?</strong
      >
    </p>
    <p class="mb-5">
      (Drag the correct item down so it is opposite the correct Branch of Government at the bottom).
    </p>
    <draggable class="list-group mb-5" :list="list1" group="people">
      <div class="list-group-item" v-for="element in list1" :key="element.name">
        <span>{{ element.name }}</span>
      </div>
    </draggable>

    <table class="table default-table">
      <tr class="header">
        <th width="50%">Branch of Government</th>
        <th>Answer</th>
      </tr>
      <tr
        class="body"
        v-for="(question, index) in questions[1]"
        :key="index"
        :class="{ wrong: !correct_answers[1]['question' + index] }"
      >
        <td>{{ question.question }}</td>
        <td>
          <draggable
            class="list-group draggable-area"
            :list="answers[1]['question' + index]"
            group="people"
            @change="removeOldSelection(1, index)"
          >
            <div
              class="list-group-item"
              v-for="element in answers[1]['question' + index]"
              :key="element.name"
            >
              <span>{{ element.name }}</span>
            </div>
          </draggable>
          <match-select
            :pool="list1"
            :answer="answers[1]['question' + index]"
            :label="question.question"
            :editable="isEditable"
          />
        </td>
      </tr>
    </table>
    <div class="overflow-hidden my-5" v-if="isEditable">
      <button class="btn btn-secondary danger btn-size-medium" @click="resetAnswers(1)">
        Reset
      </button>
    </div>

    <p class="mb-5">
      <strong>2. Choose the correct phrase to complete each of the three sentences below.</strong>
    </p>
    <p class="mb-5">(Drag the correct item down so it completes the sentence at the bottom.)</p>
    <draggable class="list-group mb-5" :list="list2" group="people">
      <div class="list-group-item" v-for="element in list2" :key="element.name">
        <span>{{ element.name }}</span>
      </div>
    </draggable>
    <div
      class="vx-row flex items-center justify-center defaultclass"
      v-for="(question, index) in questions[2]"
      :key="index"
      :class="{ wrong: !correct_answers[2]['question' + index] }"
    >
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-2">{{ question.question }}</div>
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-2">
        <draggable
          class="list-group draggable-area"
          :list="answers[2]['question' + index]"
          group="people"
          @change="removeOldSelection(2, index)"
        >
          <div
            class="list-group-item"
            v-for="element in answers[2]['question' + index]"
            :key="element.name"
          >
            <span>{{ element.name }}</span>
          </div>
        </draggable>
        <match-select
          :pool="list2"
          :answer="answers[2]['question' + index]"
          :label="question.question"
          :editable="isEditable"
        />
      </div>
    </div>

    <div class="overflow-hidden my-5" v-if="isEditable">
      <button class="btn btn-secondary danger btn-size-medium" @click="resetAnswers(2)">
        Reset
      </button>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import draggable from 'vuedraggable';
import MatchSelect from '@/components/UI/MatchSelect';
export default {
  components: { draggable, MatchSelect },
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/law/australian_legal_system_splash.jpg'),
          video: 'https://www.youtube.com/embed/g3qDRMfvzB0',
        },
      },
      answers: {
        1: {
          question1: [],
          question2: [],
          question3: [],
        },
        2: {
          question1: [],
          question2: [],
          question3: [],
        },
      },
      correct_answers: {
        1: {
          question1: true,
          question2: true,
          question3: true,
        },
        2: {
          question1: true,
          question2: true,
          question3: true,
        },
      },
      list1: [
        { name: `Interprets the law`, id: 3 },
        { name: `Makes the law`, id: 1 },
        { name: `Administers the law`, id: 2 },
      ],
      list2: [
        { name: `a parliament`, id: 2 },
        { name: `the government, including ministers`, id: 3 },
        { name: `the court system`, id: 1 },
      ],
      questions: {
        1: {
          1: { question: 'Legislature' },
          2: { question: 'Executive' },
          3: { question: 'Judiciary' },
        },
        2: {
          1: { question: 'The judiciary takes the form of' },
          2: { question: 'The legislature takes the form of' },
          3: { question: 'The executive takes the form of' },
        },
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
    removeOldSelection(group, index) {
      if (this.answers[group]['question' + index].length > 1) {
        let lastElem =
          this.answers[group]['question' + index][
            this.answers[group]['question' + index].length - 1
          ];
        for (let i = 0; i < this.answers[group]['question' + index].length - 1; i++) {
          this['list' + group].push(this.answers[group]['question' + index][i]);
        }
        this.answers[group]['question' + index] = [];
        this.answers[group]['question' + index].push(lastElem);
      }
      this.checkAnswers(group);
    },
    checkAnswers(group) {
      for (let i = 1; i <= 3; i++) {
        if (this.answers[group]['question' + i].length) {
          if (this.answers[group]['question' + i][0].id != i) {
            this.correct_answers[group]['question' + i] = false;
          } else {
            this.correct_answers[group]['question' + i] = true;
          }
        }
      }
    },
    resetAnswers(group) {
      for (let i = 1; i <= 3; i++) {
        if (this.answers[group]['question' + i].length) {
          let lastElem = this.answers[group]['question' + i][0];
          this.answers[group]['question' + i] = [];
          this.correct_answers[group]['question' + i] = true;
          this['list' + group].push(lastElem);
        }
      }
      this['list' + group] = this['list' + group].sort(() => 0.5 - Math.random());
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.data[1] !== 'undefined') {
      this.answers = this.data;

      for (let group = 1; group <= 2; group++) {
        for (let i = 1; i <= 3; i++) {
          if (this.answers[group]['question' + i].length > 0) {
            let id = this.answers[group]['question' + i][0].id;
            this['list' + group] = this['list' + group].filter(function (item) {
              return item.id !== id;
            });
          }
        }
        this.checkAnswers(group);
      }
    }
    //Shuffle the answers array
    this.list1 = this.list1.sort(() => 0.5 - Math.random());
    this.list2 = this.list2.sort(() => 0.5 - Math.random());
    this.checkAnswers(1);
    this.checkAnswers(2);
  },
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
  margin: 0;
}
.defaultclass {
  margin: 0;
}
.list-group {
  display: -ms-flexbox;
  display: -webkit-box;
  display: flex;
  -ms-flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
}
.list-group-item {
  position: relative;
  display: block;
  padding: 0.75rem 1.25rem;
  margin-bottom: -1px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
}
.list-group.draggable-area {
  border: 1px dashed #ccc;
  min-height: 45px;
}
</style>
